import React, { useState } from 'react';
import { Eye, EyeOff, Building, Shield, Users, Zap, Lock, User, CheckCircle, UserCheck, UserCog, AlertTriangle } from 'lucide-react';
import zapbooksLogo from '../assets/zapbooks-ai-logo.avif';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    mode: 'admin'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roleError, setRoleError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle WhatsApp number formatting for approver/employee modes
    if (name === 'username' && (formData.mode === 'approver' || formData.mode === 'employee')) {
      // Remove any non-digit characters
      const cleanedValue = value.replace(/\D/g, '');

      // Limit to 10 digits (after country code)
      const limitedValue = cleanedValue.slice(0, 10);

      setFormData(prev => ({
        ...prev,
        [name]: limitedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear errors when user starts typing
    if (error) setError('');
    if (roleError) setRoleError(null);
  };

  const handleModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      mode,
      username: '' // Clear username when switching modes
    }));
    // Clear errors when mode changes
    if (error) setError('');
    if (roleError) setRoleError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate inputs
    if (!formData.username || !formData.password) {
      if (formData.mode === 'admin' || formData.mode === 'super_admin') {
        setError('Please enter both username and password');
      } else {
        setError('Please enter both WhatsApp number and password');
      }
      return;
    }

    // Validate WhatsApp number for approver/employee (should be exactly 10 digits)
    if ((formData.mode === 'approver' || formData.mode === 'employee') && formData.username.length !== 10) {
      setError('Please enter a valid 10-digit WhatsApp number');
      return;
    }

    setLoading(true);
    setError('');
    setRoleError(null);

    try {
      // Prepare username - add +91 prefix for WhatsApp numbers
      let username = formData.username;
      if (formData.mode === 'approver' || formData.mode === 'employee') {
        username = `91${formData.username}`;
      }

      const requestBody = {
        username: username,
        password: formData.password,
        mode: formData.mode
      };

      console.log('Sending login request:', { ...requestBody, password: '[HIDDEN]' });

      const response = await fetch('https://api.zapbooksai.subraps.com/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        // Store auth data
        localStorage.setItem('authToken', data.data.token);
        localStorage.setItem('userData', JSON.stringify(data.data.user));
        localStorage.setItem('userCompanies', JSON.stringify(data.data.companies));

        // Call parent callback
        if (onLogin) {
          onLogin(data.data);
        }
      } else {
        // Check if it's a role mismatch error
        if (data.actualRole && data.attemptedMode) {
          setRoleError({
            actualRole: data.actualRole,
            attemptedMode: data.attemptedMode,
            message: data.message
          });
        } else {
          setError(data.message || 'Login failed');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSuggestion = () => {
    if (roleError) {
      setFormData(prev => ({
        ...prev,
        mode: roleError.actualRole
      }));
      setRoleError(null);
      setError('');
    }
  };

  const loginModes = [
    {
      id: 'super_admin',
      name: 'Super Admin',
      description: 'System administration and user management',
      icon: Shield,
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-500',
      textColor: 'text-purple-900',
      iconColor: 'text-purple-600',
      features: [''],
      credentialLabel: 'Username or Email'
    },
    {
      id: 'admin',
      name: 'Accountant',
      description: 'Full system access with all features',
      icon: UserCog,
      color: 'teal',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-500',
      textColor: 'text-teal-900',
      iconColor: 'text-teal-600',
      features: [''],
      credentialLabel: 'Username or Email'
    },
    {
      id: 'approver',
      name: 'Approver',
      description: 'Review and approve submitted transactions',
      icon: UserCheck,
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-500',
      textColor: 'text-blue-900',
      iconColor: 'text-blue-600',
      features: [''],
      credentialLabel: 'WhatsApp Number'
    },
    {
      id: 'employee',
      name: 'Employee',
      description: 'View own submitted data only',
      icon: User,
      color: 'green',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-500',
      textColor: 'text-green-900',
      iconColor: 'text-green-600',
      features: [''],
      credentialLabel: 'WhatsApp Number'
    }
  ];

  const getCredentialPlaceholder = () => {
    switch (formData.mode) {
      case 'super_admin':
        return 'Enter super admin username or email';
      case 'admin':
        return 'Enter admin username or email';
      case 'approver':
      case 'employee':
        return 'Enter 10-digit WhatsApp number';
      default:
        return 'Enter your credentials';
    }
  };

  const selectedMode = loginModes.find(mode => mode.id === formData.mode);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center p-2">
      <div className="max-w-4xl w-full">
        {/* Header with Logo and Title */}
        <div className="text-center mb-6">
          <img
            src={zapbooksLogo}
            alt="ZapBooks AI Logo"
            className="h-14 w-auto mx-auto mb-3 mix-blend-multiply"
          />
        </div>

        {/* Mode Selection */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Select Login Mode</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {loginModes.map((mode) => {
              const IconComponent = mode.icon;
              const isSelected = formData.mode === mode.id;

              return (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => handleModeChange(mode.id)}
                  className={`p-4 border-2 rounded-lg transition-all text-left ${isSelected
                    ? `${mode.borderColor} ${mode.bgColor}`
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <IconComponent className={`w-5 h-5 ${isSelected ? mode.iconColor : 'text-gray-400'}`} />
                    <span className={`font-medium ${isSelected ? mode.textColor : 'text-gray-700'}`}>
                      {mode.name}
                    </span>
                    {isSelected && <CheckCircle className={`w-4 h-4 ${mode.iconColor} ml-auto`} />}
                  </div>
                  <p className={`text-xs ${isSelected ? mode.textColor : 'text-gray-500'}`}>
                    {mode.description}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {mode.features.map((feature, index) => (
                      <span
                        key={index}
                        className={`inline-block px-2 py-1 text-xs rounded ${isSelected
                          ? `${mode.bgColor} ${mode.textColor}`
                          : 'bg-gray-100 text-gray-600'
                          }`}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 max-w-lg mx-auto">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
              {selectedMode && React.createElement(selectedMode.icon, {
                className: `w-5 h-5 ${selectedMode.iconColor}`
              })}
              Welcome Back - {selectedMode?.name}
            </h2>
          </div>

          {/* Role Mismatch Error */}
          {roleError && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-amber-800 font-medium mb-1">Wrong Login Mode</h3>
                  <p className="text-amber-700 text-sm mb-3">{roleError.message}</p>
                  <button
                    onClick={handleRoleSuggestion}
                    className="bg-amber-600 text-white px-4 py-2 rounded-md text-sm hover:bg-amber-700 transition-colors"
                  >
                    Switch to {roleError.actualRole.charAt(0).toUpperCase() + roleError.actualRole.slice(1)} Mode
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Regular Error */}
          {error && !roleError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm flex items-center gap-2">
                <Shield className="w-4 h-4" />
                {error}
              </p>
            </div>
          )}

          <div className="space-y-4">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                {selectedMode?.credentialLabel}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />

                {/* Show +91 prefix for WhatsApp number inputs */}
                {(formData.mode === 'approver' || formData.mode === 'employee') && (
                  <div className="absolute left-10 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm font-medium">
                    +91
                  </div>
                )}

                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`w-full ${(formData.mode === 'approver' || formData.mode === 'employee') ? 'pl-16' : 'pl-10'} pr-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors`}
                  placeholder={getCredentialPlaceholder()}
                  required
                  disabled={loading}
                  maxLength={formData.mode === 'admin' || formData.mode === 'super_admin' ? undefined : 10}
                />
              </div>

              {/* Helper text for WhatsApp number */}
              {(formData.mode === 'approver' || formData.mode === 'employee') && (
                <p className="text-xs text-gray-500 mt-1">
                  Enter your 10-digit WhatsApp number. +91 will be added automatically.
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Info message for approver/employee */}
            {(formData.mode === 'approver' || formData.mode === 'employee') && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-blue-700 text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Your WhatsApp number will be sent as +91{formData.username} for authentication.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="w-full bg-gradient-to-r from-teal-600 to-blue-600 text-white py-2.5 px-4 rounded-md hover:from-teal-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Signing In...
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  {selectedMode && React.createElement(selectedMode.icon, { className: "w-4 h-4" })}
                  Sign In as {selectedMode?.name}
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <p>© 2025 ZapBooksAI @ Subraps - Secure Multi-Company Accounting</p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              Zoho Integration
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              JWT Secured
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              Multi-Tenant
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;